/**
 * ============================================================================
 * CapasTable — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: list of CAPAs opened against the supplier. Highlights severity
 *          and the effectiveness-verification (EV) cohort because closure
 *          alone is not enough — we want to track whether the corrective
 *          action actually held.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import wstyles from '../WebhookAdmin/WebhookAdmin.module.css';
import styles from './SupplierDetail.module.css';
import type { CapaRow, CapaSeverity, CapaStatus } from './useSupplierDetailQuery';

const SEV_CLASS: Record<CapaSeverity, string> = {
  MINOR: styles.critLow,
  MAJOR: styles.critMajor,
  CRITICAL: styles.critCritical,
};

const STATUS_CLASS: Record<CapaStatus, string> = {
  OPEN: styles.critMajor,
  IN_PROGRESS: styles.lifeRestricted,
  COMPLETED: styles.lifeActive,
  VERIFIED: styles.lifeActive,
  CLOSED: styles.lifeActive,
};

export interface CapasTableProps {
  rows: CapaRow[];
}

export function CapasTable({ rows }: CapasTableProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }),
    [i18n.language],
  );

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('supplier.detail.capas.empty', {
          defaultValue: 'No CAPAs opened against this supplier.',
        })}
      </p>
    );
  }

  return (
    <table
      className={wstyles.table}
      aria-label={t('supplier.detail.capas.tableAria', {
        defaultValue: 'Supplier CAPAs',
      })}
    >
      <caption className={styles.srOnly}>
        {t('supplier.detail.capas.tableCaption', {
          defaultValue:
            'CAPA list with severity, status, opened date, and effectiveness verification cohort.',
        })}
      </caption>
      <thead>
        <tr>
          <th scope="col">
            {t('supplier.detail.capas.columns.title', {
              defaultValue: 'Title',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.capas.columns.severity', {
              defaultValue: 'Severity',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.capas.columns.status', {
              defaultValue: 'Status',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.capas.columns.opened', {
              defaultValue: 'Opened',
            })}
          </th>
          <th scope="col">
            {t('supplier.detail.capas.columns.ev', {
              defaultValue: 'EV cohort',
            })}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((c) => (
          <tr key={c.capa_id}>
            <td>
              <strong>{c.title}</strong>
              <div className={styles.subdued}>{c.capa_id}</div>
            </td>
            <td>
              <span
                className={`${styles.chip} ${SEV_CLASS[c.severity]}`}
                aria-label={t('supplier.detail.capas.severityAria', {
                  defaultValue: 'Severity: {{s}}',
                  s: t(`supplier.detail.capas.severity.${c.severity}`, {
                    defaultValue: c.severity,
                  }),
                })}
              >
                {t(`supplier.detail.capas.severity.${c.severity}`, {
                  defaultValue: c.severity,
                })}
              </span>
            </td>
            <td>
              <span
                className={`${styles.chip} ${STATUS_CLASS[c.status]}`}
                aria-label={t('supplier.detail.capas.statusAria', {
                  defaultValue: 'Status: {{s}}',
                  s: t(`supplier.detail.capas.status.${c.status}`, {
                    defaultValue: c.status,
                  }),
                })}
              >
                {t(`supplier.detail.capas.status.${c.status}`, {
                  defaultValue: c.status,
                })}
              </span>
            </td>
            <td>
              <time dateTime={c.opened_at_utc}>
                {dateFmt.format(new Date(c.opened_at_utc))}
              </time>
            </td>
            <td>
              {c.effectiveness_cohort ? (
                <span className={styles.codeChip}>
                  {t(`supplier.detail.capas.ev.${c.effectiveness_cohort}`, {
                    defaultValue: c.effectiveness_cohort,
                  })}
                </span>
              ) : (
                <span className={styles.subdued}>—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
